import React, { useMemo, useState, useEffect } from "react";

const TODAY = new Date();
TODAY.setUTCHours(0, 0, 0, 0);

function toF(c) {
  return (c * 9) / 5 + 32;
}
function convert(val, unit) {
  return unit === "F" ? toF(val) : val;
}
function convertDelta(val, unit) {
  return unit === "F" ? (val * 9) / 5 : val;
}

function shortDate(date) {
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}
function weekday(date) {
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    timeZone: "UTC",
  });
}

const BAR_MAX = 72;

// WeatherStrip is now driven by the backend-built `weatherStripData` feed.
// Expected shape:
// [
//   {
//     date: "YYYY-MM-DD",
//     actual: number | null,
//     baseline_avg: number | null,
//     actual_anomaly: number | null,
//     source: "ghcn" | "nws" | "forecast" | null,
//     is_observed: boolean,
//     is_featured: boolean,
//     strip_position: -5..5
//   }
// ]
export function WeatherStrip({ weatherStripData, unit }) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  const positionLimit = isMobile ? 3 : 5;

  const { past, featured, future } = useMemo(() => {
    if (!weatherStripData?.length)
      return { past: [], featured: null, future: [] };

    // The backend already sends the 11-day centered window; the frontend only
    // needs to sort it and split it around the featured day.
    const all = weatherStripData
      .map((d) => ({ ...d, date: new Date(`${d.date}T00:00:00Z`) }))
      .sort((a, b) => a.strip_position - b.strip_position);

    const featured = all.find((d) => d.is_featured) ?? null;
    const past = all.filter((d) => d.strip_position < 0 && d.strip_position >= -positionLimit);
    const future = all.filter((d) => d.strip_position > 0 && d.strip_position <= positionLimit);

    return { past, featured, future };
  }, [weatherStripData, positionLimit]);

  const days = [...past, ...(featured ? [featured] : []), ...future];
  if (!days.length) return null;

  // Missing days are kept in the backend table on purpose so data gaps are
  // visible instead of silently disappearing from the strip.
  const validAnomalies = days
    .map((d) => d.actual_anomaly)
    .filter((value) => value != null)
    .map((value) => Math.abs(value));
  const maxAbs = Math.max(...validAnomalies, 0.1);
  const barH = (anomaly) => Math.round((Math.abs(anomaly) / maxAbs) * BAR_MAX);

  const isActuallyToday = featured?.date.getTime() === TODAY.getTime();
  const heroHasData =
    featured?.actual != null && featured?.actual_anomaly != null;
  const heroTemp = heroHasData
    ? convert(featured.actual, unit).toFixed(1)
    : null;
  const heroAnomaly = heroHasData
    ? convertDelta(featured.actual_anomaly, unit)
    : null;
  const heroIsHot = heroHasData ? featured.actual_anomaly >= 0 : true;

  return (
    <section className="weather-strip-section">
      {/* Hero block */}
      {featured && (
        <div className="strip-hero">
          <div className="strip-hero-label">
            {isActuallyToday
              ? "TODAY'S TEMPERATURE"
              : `${shortDate(featured.date).toUpperCase()} — LATEST READING`}
          </div>
          {heroHasData ? (
            <>
              <div
                className={`strip-hero-temp ${heroIsHot ? "temp-hot" : "temp-cool"}`}
              >
                {heroTemp}°{unit}
              </div>
              <div className="strip-hero-sub">
                <span className={heroIsHot ? "temp-hot" : "temp-cool"}>
                  {Math.abs(heroAnomaly).toFixed(1)}°{unit}{" "}
                  {heroIsHot ? "above" : "below"}
                </span>{" "}
                the 1951–80 historical average
              </div>
            </>
          ) : (
            <div className="strip-hero-sub">
              No reading is available for this day.
            </div>
          )}
        </div>
      )}

      {/* Label */}
      <div className="weather-strip-header">
        <span className="weather-strip-title">5 days past &amp; ahead</span>
      </div>

      {/* Hot bars — above cards */}
      <div className="strip-grid strip-bars-top">
        {days.map((d) => (
          <div key={d.date.toISOString()} className="bar-cell bar-cell--top">
            {d.actual_anomaly != null && d.actual_anomaly > 0 && (
              <div
                className="ws-bar ws-bar--hot"
                style={{ height: barH(d.actual_anomaly) }}
              />
            )}
          </div>
        ))}
      </div>

      {/* Cards */}
      <div className="strip-grid">
        {days.map((d) => {
          const isFeatured =
            featured && d.date.getTime() === featured.date.getTime();
          const isForecast =
            typeof d.is_observed === "boolean"
              ? !d.is_observed
              : d.date > TODAY;
          const hasData = d.actual != null && d.actual_anomaly != null;
          const temp = hasData ? convert(d.actual, unit).toFixed(1) : null;
          const anomaly = hasData ? convertDelta(d.actual_anomaly, unit) : null;
          const isHot = hasData ? d.actual_anomaly >= 0 : true;

          return (
            <div
              key={d.date.toISOString()}
              className={[
                "dc",
                isFeatured ? "dc--today" : "",
                isForecast && !isFeatured ? "dc--forecast" : "",
              ]
                .join(" ")
                .trim()}
            >
              <div
                className={`dc-label ${isFeatured ? "dc-label--today" : ""}`}
              >
                {isFeatured
                  ? "TODAY"
                  : isForecast
                    ? "FCST"
                    : weekday(d.date)}
              </div>
              {!isFeatured && (
                <div className="dc-date">{shortDate(d.date)}</div>
              )}
              {hasData ? (
                <>
                  <div
                    className={`dc-temp ${isHot ? "temp-hot" : "temp-cool"}`}
                  >
                    {temp}°{unit}
                  </div>
                  <div
                    className={`dc-anomaly ${isHot ? "temp-hot" : "temp-cool"}`}
                  >
                    {anomaly >= 0 ? "▲" : "▼"}
                    {Math.abs(anomaly).toFixed(1)}°
                  </div>
                </>
              ) : (
                <>
                  <div className="dc-temp">--</div>
                  <div className="dc-anomaly">No data</div>
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Cool bars — below cards */}
      <div className="strip-grid strip-bars-bottom">
        {days.map((d) => (
          <div key={d.date.toISOString()} className="bar-cell bar-cell--bottom">
            {d.actual_anomaly != null && d.actual_anomaly < 0 && (
              <div
                className="ws-bar ws-bar--cool"
                style={{ height: barH(d.actual_anomaly) }}
              />
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
